import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { Navbar } from '../../components/feature/Navbar';
import { PropertyCard } from '../../components/feature/PropertyCard';
import { featuredProperties, shortTermProperties } from '../../mocks/properties';
import { testimonials } from '../../mocks/testimonials';

// Contact Form Component
const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d60scon4klnl5oj67ft0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as Record<string, string>).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form 
      id="contact-team-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-4 sm:space-y-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">Full Name</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Your name"
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-200 rounded-lg sm:rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">Email Address</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="your@email.com"
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-200 rounded-lg sm:rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">Phone Number</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+211 XXX XXX XXX"
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-200 rounded-lg sm:rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">Subject</label>
          <select
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-200 rounded-lg sm:rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all cursor-pointer appearance-none"
          >
            <option value="">Select a subject</option>
            <option value="Property Inquiry">Property Inquiry</option>
            <option value="Schedule Viewing">Schedule a Viewing</option>
            <option value="List My Property">List My Property</option>
            <option value="Short-Term Rental">Short-Term Rental Inquiry</option>
            <option value="General Question">General Question</option>
            <option value="Partnership">Partnership Opportunity</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">Message</label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          maxLength={500}
          rows={4}
          placeholder="Tell us how we can help you..."
          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-200 rounded-lg sm:rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all resize-none"
        />
        <p className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</p>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-3 sm:py-4 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-bold rounded-lg sm:rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap shadow-lg shadow-primary-500/30 hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed text-sm sm:text-base"
      >
        {isSubmitting ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            Sending...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line"></i>
            Send Message
          </>
        )}
      </button>

      {submitStatus === 'success' && (
        <div className="flex items-center gap-2 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg sm:rounded-xl text-green-700">
          <i className="ri-checkbox-circle-line text-lg sm:text-xl"></i>
          <p className="text-xs sm:text-sm font-medium">Thank you! We'll get back to you soon.</p>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="flex items-center gap-2 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg sm:rounded-xl text-red-700">
          <i className="ri-error-warning-line text-lg sm:text-xl"></i>
          <p className="text-xs sm:text-sm font-medium">Something went wrong. Please try again.</p>
        </div>
      )}
    </form>
  );
};

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [priceRange, setPriceRange] = useState('');
  const [location, setLocation] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [listingType, setListingType] = useState('all');
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const locations = [
    'All Locations',
    'Juba',
    'Thongpiny',
    'Gudele',
    'Munuki',
    'Kator',
    'Hai Referendum',
    'Hai Malakal',
    'Tongping',
    'Nyakuron',
  ];

  const priceRanges = {
    all: [
      { label: 'Any Price', value: '' },
      { label: 'Under $500/mo', value: '0-500' },
      { label: '$500 - $1,000/mo', value: '500-1000' },
      { label: '$1,000 - $2,000/mo', value: '1000-2000' },
      { label: '$2,000 - $5,000/mo', value: '2000-5000' },
      { label: 'Over $5,000/mo', value: '5000+' },
    ],
    rent: [
      { label: 'Any Price', value: '' },
      { label: 'Under $500/mo', value: '0-500' },
      { label: '$500 - $1,000/mo', value: '500-1000' },
      { label: '$1,000 - $2,000/mo', value: '1000-2000' },
      { label: 'Over $2,000/mo', value: '2000+' },
    ],
    sale: [
      { label: 'Any Price', value: '' },
      { label: 'Under $50K', value: '0-50000' },
      { label: '$50K - $100K', value: '50000-100000' },
      { label: '$100K - $200K', value: '100000-200000' },
      { label: 'Over $200K', value: '200000+' },
    ],
    'short-term': [
      { label: 'Any Price', value: '' },
      { label: 'Under $50/night', value: '0-50' },
      { label: '$50 - $100/night', value: '50-100' },
      { label: '$100 - $200/night', value: '100-200' },
      { label: 'Over $200/night', value: '200+' },
    ],
  };

  const propertyTypes = [
    'All Types',
    'Apartment',
    'House',
    'Villa',
    'Commercial',
    'Land',
  ];

  const categories = [
    {
      icon: 'ri-home-smile-line',
      label: 'Houses',
      count: '50+',
      color: 'from-orange-400 to-red-500',
    },
    {
      icon: 'ri-building-line',
      label: 'Apartments',
      count: '30+',
      color: 'from-emerald-400 to-teal-500',
    },
    {
      icon: 'ri-home-wifi-line',
      label: 'Short-Term',
      count: '25+',
      color: 'from-amber-400 to-orange-500',
      isShortTerm: true,
    },
    {
      icon: 'ri-store-2-line',
      label: 'Commercial',
      count: '15+',
      color: 'from-rose-400 to-pink-500',
    },
    {
      icon: 'ri-landscape-line',
      label: 'Land',
      count: '25+',
      color: 'from-cyan-400 to-teal-500',
    },
  ];

  const steps = [
    {
      icon: 'ri-search-line',
      title: 'Search Properties',
      desc: 'Browse our extensive collection of properties in Juba and find your perfect match.',
    },
    {
      icon: 'ri-phone-line',
      title: 'Contact Agent',
      desc: 'Connect directly with verified agents who will guide you through the process.',
    },
    {
      icon: 'ri-key-2-line',
      title: 'Get Your Keys',
      desc: 'Complete the paperwork and move into your new home hassle-free.',
    },
  ];

  const handleSearch = () => {
    try {
      const params = new URLSearchParams();
      if (priceRange) params.set('price', priceRange);
      if (location && location !== 'All Locations') params.set('location', location);
      if (propertyType && propertyType !== 'All Types') params.set('type', propertyType);
      if (listingType && listingType !== 'all') params.set('listing', listingType);
      navigate(`/listings?${params.toString()}`);
    } catch (error) {
      console.error('Failed to navigate to listings:', error);
    }
  };

  const handleListingTypeChange = (type: string) => {
    setListingType(type);
    setPriceRange('');
  };

  const currentPriceRanges = priceRanges[listingType as keyof typeof priceRanges] || priceRanges.all;

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[85vh] sm:min-h-[75vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/8bfbec1a06f210b0b0f58cdffa74abce.jpg"
            alt="Juba City South Sudan"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>

        {/* Animated Floating Elements - Hidden on mobile */}
        <div className="hidden sm:block absolute top-32 left-10 w-20 h-20 bg-primary-500/20 rounded-full blur-xl animate-pulse"></div>
        <div className="hidden sm:block absolute bottom-40 right-20 w-32 h-32 bg-amber-500/20 rounded-full blur-xl animate-pulse delay-1000"></div>

        <div
          className={`relative z-10 text-center px-4 sm:px-6 w-full max-w-5xl transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-4 sm:mb-6">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            <span className="text-white/90 text-xs sm:text-sm font-medium">
              South Sudan&apos;s Premier Real Estate Platform
            </span>
          </div>

          <h1 className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold tracking-tight mb-4 sm:mb-6 leading-tight">
            Find Your Perfect
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">
              Home In Juba
            </span>
          </h1>
          <p className="text-white/80 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-6 sm:mb-10 px-4">
            Discover premium properties for rent, sale, and short-term stays across South Sudan.
          </p>

          {/* Search Filters */}
          <div className="bg-white/95 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 max-w-4xl mx-auto">
            {/* Listing Type Tabs - Horizontal scroll on mobile */}
            <div className="flex items-center gap-2 mb-4 sm:mb-6 overflow-x-auto pb-2 hide-scrollbar -mx-1 px-1">
              <button 
                onClick={() => handleListingTypeChange('all')}
                className={`px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold cursor-pointer whitespace-nowrap transition-all flex-shrink-0 ${
                  listingType === 'all' 
                    ? 'bg-primary-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                All
              </button>
              <button 
                onClick={() => handleListingTypeChange('rent')}
                className={`px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold cursor-pointer whitespace-nowrap transition-all flex-shrink-0 ${
                  listingType === 'rent' 
                    ? 'bg-primary-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                For Rent
              </button>
              <button 
                onClick={() => handleListingTypeChange('sale')}
                className={`px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold cursor-pointer whitespace-nowrap transition-all flex-shrink-0 ${
                  listingType === 'sale' 
                    ? 'bg-primary-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                For Sale
              </button>
              <button 
                onClick={() => handleListingTypeChange('short-term')}
                className={`px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold cursor-pointer whitespace-nowrap transition-all flex items-center gap-1.5 flex-shrink-0 ${
                  listingType === 'short-term' 
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white' 
                    : 'text-gray-600 hover:bg-amber-50 border border-amber-200'
                }`}
              >
                <i className="ri-home-wifi-line"></i>
                <span className="hidden xs:inline">Short-Term</span>
                <span className="xs:hidden">Airbnb</span>
              </button>
            </div>

            {/* Short-term info banner */}
            {listingType === 'short-term' && (
              <div className="mb-4 sm:mb-6 p-2.5 sm:p-3 bg-amber-50 border border-amber-200 rounded-lg sm:rounded-xl flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-home-wifi-line text-amber-600 text-base sm:text-lg"></i>
                </div>
                <div className="text-left min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-amber-800">Short-Term & Airbnb Rentals</p>
                  <p className="text-xs text-amber-600 hidden sm:block">Furnished apartments and homes for daily, weekly, or monthly stays</p>
                </div>
              </div>
            )}

            {/* Filter Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Location Filter */}
              <div className="relative">
                <label className="block text-left text-xs font-semibold text-gray-500 mb-1.5 sm:mb-2 uppercase tracking-wide">
                  Location
                </label>
                <div className="relative">
                  <i className="ri-map-pin-line absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-primary-500 text-base sm:text-lg"></i>
                  <select
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 bg-gray-50 border-2 border-gray-100 rounded-lg sm:rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 cursor-pointer appearance-none font-medium transition-all"
                  >
                    {locations.map((loc) => (
                      <option key={loc} value={loc}>
                        {loc}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                </div>
              </div>

              {/* Property Type Filter */}
              <div className="relative">
                <label className="block text-left text-xs font-semibold text-gray-500 mb-1.5 sm:mb-2 uppercase tracking-wide">
                  Property Type
                </label>
                <div className="relative">
                  <i className="ri-home-line absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-primary-500 text-base sm:text-lg"></i>
                  <select
                    value={propertyType}
                    onChange={(e) => setPropertyType(e.target.value)}
                    className="w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 bg-gray-50 border-2 border-gray-100 rounded-lg sm:rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 cursor-pointer appearance-none font-medium transition-all"
                  >
                    {propertyTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                </div>
              </div>

              {/* Price Range Filter */}
              <div className="relative">
                <label className="block text-left text-xs font-semibold text-gray-500 mb-1.5 sm:mb-2 uppercase tracking-wide">
                  {listingType === 'short-term' ? 'Price / Night' : 'Price Range'}
                </label>
                <div className="relative">
                  <i className="ri-money-dollar-circle-line absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-primary-500 text-base sm:text-lg"></i>
                  <select
                    value={priceRange}
                    onChange={(e) => setPriceRange(e.target.value)}
                    className="w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 bg-gray-50 border-2 border-gray-100 rounded-lg sm:rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 cursor-pointer appearance-none font-medium transition-all"
                  >
                    {currentPriceRanges.map((range) => (
                      <option key={range.value} value={range.value}>
                        {range.label}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                </div>
              </div>

              {/* Search Button */}
              <div className="flex items-end">
                <button
                  onClick={handleSearch}
                  className="w-full py-3 sm:py-4 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-bold rounded-lg sm:rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transform hover:-translate-y-0.5 text-sm sm:text-base"
                >
                  <i className="ri-search-line text-lg"></i>
                  Search
                </button>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="flex flex-wrap justify-center gap-6 sm:gap-8 mt-6 sm:mt-10">
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-white">500+</p>
              <p className="text-white/70 text-xs sm:text-sm">Properties Listed</p>
            </div>
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-white">200+</p>
              <p className="text-white/70 text-xs sm:text-sm">Happy Clients</p>
            </div>
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-white">50+</p>
              <p className="text-white/70 text-xs sm:text-sm">Expert Agents</p>
            </div>
          </div>
        </div>

        {/* Scroll Indicator - Hidden on mobile */}
        <div className="hidden sm:block absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/70 rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Property Categories */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 animate-fade-in-up">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Browse by Category</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base px-4">
              Explore our diverse range of properties tailored to your needs
            </p>
          </div>

          {/* Categories - Horizontal scroll on mobile, grid on desktop */}
          <div className="flex sm:grid sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-6 overflow-x-auto sm:overflow-visible pb-4 sm:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0 hide-scrollbar">
            {categories.map((cat, index) => (
              <Link
                key={index}
                to={cat.isShortTerm ? '/listings?listing=short-term' : `/listings?type=${cat.label.toLowerCase()}`}
                className="group relative bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm hover:shadow-2xl transition-all duration-500 cursor-pointer overflow-hidden border border-gray-100 card-hover min-w-[140px] sm:min-w-0 flex-shrink-0"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${cat.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                {cat.isShortTerm && (
                  <div className="absolute top-2 right-2 bg-amber-100 text-amber-700 text-xs font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                    Airbnb
                  </div>
                )}
                <div className={`w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-br ${cat.color} mb-3 sm:mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                  <i className={`${cat.icon} text-xl sm:text-2xl text-white`}></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-0.5 sm:mb-1 group-hover:text-coral-600 transition-colors duration-300 text-sm sm:text-base">{cat.label}</h3>
                <p className="text-xs sm:text-sm text-gray-500">{cat.count} Properties</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Short-Term Rentals Section */}
      <section className="px-4 sm:px-6 lg:px-12 py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 sm:mb-12 gap-4 animate-fade-in-up">
            <div>
              <div className="flex items-center gap-3 mb-2 sm:mb-3">
                <span className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 rounded-full text-xs sm:text-sm font-semibold">
                  <i className="ri-home-wifi-line"></i>
                  Short-Term Stays
                </span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900">Airbnb & Short-Term Rentals</h2>
              <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">Fully furnished properties for daily, weekly, or monthly stays</p>
            </div>
            <Link
              to="/listings?listing=short-term"
              className="group flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full font-semibold hover:from-amber-600 hover:to-orange-600 transition-all duration-300 cursor-pointer whitespace-nowrap hover:-translate-y-1 hover:shadow-xl btn-press text-sm sm:text-base"
            >
              View All
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
            </Link>
          </div>

          {/* Property Cards - Horizontal scroll on mobile */}
          <div className="flex lg:grid lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 overflow-x-auto lg:overflow-visible pb-4 lg:pb-0 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0 hide-scrollbar" data-product-shop>
            {shortTermProperties.slice(0, 3).map((property) => (
              <div key={property.id} className="min-w-[280px] sm:min-w-[320px] lg:min-w-0 flex-shrink-0 lg:flex-shrink">
                <PropertyCard
                  id={property.id}
                  title={property.title}
                  location={property.location}
                  address={property.address}
                  price={property.price}
                  currency={property.currency}
                  bedrooms={property.bedrooms}
                  bathrooms={property.bathrooms}
                  parking={property.parking}
                  area={property.area}
                  image={property.image}
                  ribbon={property.ribbon}
                  listingType={property.listingType}
                  priceUnit={property.priceUnit}
                  maxGuests={property.maxGuests}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="px-4 sm:px-6 lg:px-12 py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 sm:mb-12 gap-4 animate-fade-in-up">
            <div>
              <span className="inline-block px-3 sm:px-4 py-1 bg-primary-100 text-primary-700 rounded-full text-xs sm:text-sm font-semibold mb-2 sm:mb-3">
                Featured
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900">Premium Properties</h2>
              <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">Handpicked selection of our finest listings</p>
            </div>
            <Link
              to="/listings"
              className="group flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-900 text-white rounded-full font-semibold hover:bg-gray-800 transition-all duration-300 cursor-pointer whitespace-nowrap hover:-translate-y-1 hover:shadow-xl btn-press text-sm sm:text-base"
            >
              View All
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
            </Link>
          </div>

          {/* Property Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8" data-product-shop>
            {featuredProperties.map((property) => (
              <PropertyCard
                key={property.id}
                id={property.id}
                title={property.title}
                location={property.location}
                address={property.address}
                price={property.price}
                currency={property.currency}
                bedrooms={property.bedrooms}
                bathrooms={property.bathrooms}
                parking={property.parking}
                area={property.area}
                image={property.image}
                ribbon={property.ribbon}
                listingType={property.listingType}
              />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-12 bg-gray-900 relative overflow-hidden">
        <div className="hidden sm:block absolute top-0 left-0 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-float"></div>
        <div className="hidden sm:block absolute bottom-0 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-float animation-delay-500"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-10 sm:mb-16 animate-fade-in-up">
            <span className="inline-block px-3 sm:px-4 py-1 bg-white/10 text-white rounded-full text-xs sm:text-sm font-semibold mb-2 sm:mb-3">
              Simple Process
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">How It Works</h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base px-4">Finding your dream property has never been easier</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative group">
                <div className="bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-white/10 hover:border-primary-500/50 transition-all duration-500 hover:bg-white/10 card-hover">
                  <div className="absolute -top-3 -left-3 sm:-top-4 sm:-left-4 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg group-hover:scale-110 transition-transform duration-300 text-sm sm:text-base">
                    {index + 1}
                  </div>
                  <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-gradient-to-br from-primary-500/20 to-primary-600/20 rounded-xl sm:rounded-2xl mb-4 sm:mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                    <i className={`${step.icon} text-2xl sm:text-3xl text-primary-400`}></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">{step.title}</h3>
                  <p className="text-gray-400 leading-relaxed text-sm sm:text-base">{step.desc}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden sm:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-primary-500 to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 animate-fade-in-up">
            <span className="inline-block px-3 sm:px-4 py-1 bg-primary-100 text-primary-700 rounded-full text-xs sm:text-sm font-semibold mb-2 sm:mb-3">
              Testimonials
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">What Our Clients Say</h2>
            <p className="text-gray-600 max-w-xl mx-auto text-sm sm:text-base px-4">
              Trusted by hundreds of happy homeowners across South Sudan
            </p>
          </div>

          {/* Slideshow Container */}
          <div className="relative px-2 sm:px-0">
            {/* Main Testimonial Card */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border border-gray-100 p-6 sm:p-8 md:p-12 transition-all duration-500">
              <div className="flex flex-col items-center text-center">
                {/* Quote Icon */}
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-primary-100 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                  <i className="ri-double-quotes-l text-xl sm:text-2xl text-primary-600"></i>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-1 mb-4 sm:mb-6">
                  {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-lg sm:text-xl"></i>
                  ))}
                </div>

                {/* Comment */}
                <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed mb-6 sm:mb-8 max-w-2xl">
                  &ldquo;{testimonials[currentTestimonial].comment}&rdquo;
                </p>

                {/* User Info */}
                <div className="flex flex-col items-center">
                  <img
                    src={`https://readdy.ai/api/search-image?query=Professional African ${currentTestimonial % 2 === 0 ? 'man' : 'woman'} portrait headshot natural smile warm expression neutral background high quality photography&width=120&height=120&seq=testimonial-slide-${testimonials[currentTestimonial].id}&orientation=squarish`}
                    alt={testimonials[currentTestimonial].name}
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover object-top ring-4 ring-primary-100 mb-3 sm:mb-4"
                  />
                  <h4 className="text-base sm:text-lg font-bold text-gray-900">
                    {testimonials[currentTestimonial].name}
                  </h4>
                  <p className="text-gray-600 text-xs sm:text-sm">{testimonials[currentTestimonial].role}</p>
                  <div className="flex items-center gap-1 text-gray-400 text-xs sm:text-sm mt-1">
                    <i className="ri-map-pin-line"></i>
                    <span>{testimonials[currentTestimonial].location}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={() => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
              className="absolute left-0 sm:-left-4 md:-left-6 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full shadow-lg border border-gray-100 flex items-center justify-center text-gray-600 hover:text-primary-600 hover:border-primary-200 transition-all cursor-pointer z-10"
            >
              <i className="ri-arrow-left-s-line text-xl sm:text-2xl"></i>
            </button>
            <button
              onClick={() => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)}
              className="absolute right-0 sm:-right-4 md:-right-6 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full shadow-lg border border-gray-100 flex items-center justify-center text-gray-600 hover:text-primary-600 hover:border-primary-200 transition-all cursor-pointer z-10"
            >
              <i className="ri-arrow-right-s-line text-xl sm:text-2xl"></i>
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex items-center justify-center gap-2 mt-6 sm:mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`h-2 sm:h-2.5 rounded-full transition-all cursor-pointer ${
                  index === currentTestimonial
                    ? 'bg-primary-600 w-6 sm:w-8'
                    : 'bg-gray-300 hover:bg-gray-400 w-2 sm:w-2.5'
                }`}
              />
            ))}
          </div>

          {/* View All Link */}
          <div className="text-center mt-8 sm:mt-10">
            <Link
              to="/testimonials"
              className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors cursor-pointer group text-sm sm:text-base"
            >
              Read All Reviews
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            {/* Left Side - Info */}
            <div className="animate-fade-in-left">
              <span className="inline-block px-3 sm:px-4 py-1 bg-primary-100 text-primary-700 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                Get In Touch
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                Connect With Our Team
              </h2>
              <p className="text-gray-600 text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed">
                Have questions about a property or need help finding your perfect home? Our expert team is here to assist you every step of the way.
              </p>

              {/* Contact Cards */}
              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl hover:bg-primary-50 transition-colors group">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-primary-100 rounded-lg sm:rounded-xl group-hover:bg-primary-200 transition-colors flex-shrink-0">
                    <i className="ri-phone-line text-lg sm:text-xl text-primary-600"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-500">Call Us Directly</p>
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">+211 912 000 000</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl hover:bg-primary-50 transition-colors group">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-primary-100 rounded-lg sm:rounded-xl group-hover:bg-primary-200 transition-colors flex-shrink-0">
                    <i className="ri-mail-line text-lg sm:text-xl text-primary-600"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-500">Email Us</p>
                    <p className="font-semibold text-gray-900 text-sm sm:text-base truncate">info@juwa.ss</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl hover:bg-primary-50 transition-colors group">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-primary-100 rounded-lg sm:rounded-xl group-hover:bg-primary-200 transition-colors flex-shrink-0">
                    <i className="ri-map-pin-line text-lg sm:text-xl text-primary-600"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-500">Visit Our Office</p>
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">Juba, South Sudan</p>
                  </div>
                </div>
              </div>

              {/* Team Preview */}
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="flex -space-x-2 sm:-space-x-3">
                  <img
                    src="https://static.readdy.ai/image/8b99d679a4d9fbd968419a3fb7823edc/c70cbb03d41e3965f222b05b10e0bfbe.jpeg"
                    alt="Julie Cholhok"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-white object-cover"
                  />
                  <img
                    src="https://static.readdy.ai/image/8b99d679a4d9fbd968419a3fb7823edc/c0339d9f7de876f94c9e9d086608da6c.jpeg"
                    alt="Chan Isaiah Diing"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-white object-cover"
                  />
                  <img
                    src="https://static.readdy.ai/image/8b99d679a4d9fbd968419a3fb7823edc/3df8a572a3607e408948e8d8276bf850.jpeg"
                    alt="Marjorie Yar Hennepin"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-white object-cover"
                  />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">
                    <span className="font-semibold text-gray-900">Expert team</span> ready to help you
                  </p>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="bg-gray-50 rounded-2xl sm:rounded-3xl p-5 sm:p-8 md:p-10 shadow-sm border border-gray-100">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">Send Us a Message</h3>
              <p className="text-gray-600 text-xs sm:text-sm mb-4 sm:mb-6">Fill out the form and we'll get back to you within 24 hours.</p>

              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-12 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Beautiful%20modern%20African%20home%20interior%20living%20room%20with%20warm%20natural%20lighting%20comfortable%20furniture%20tropical%20plants%20through%20windows%20Juba%20South%20Sudan%20style%20contemporary%20design%20earth%20tones%20professional%20interior%20photography&width=1920&height=600&seq=jubacta2025&orientation=landscape"
            alt="Beautiful Home Interior"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-2xl animate-fade-in-left">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight">
              Ready to Find Your Dream Home?
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 sm:mb-8">
              Join thousands of satisfied customers who found their perfect property through JUWA Real Estate.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/listings"
                className="px-6 sm:px-8 py-3 sm:py-4 bg-white text-gray-900 font-bold rounded-lg sm:rounded-xl hover:bg-gray-100 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:-translate-y-1 hover:shadow-xl btn-press text-center text-sm sm:text-base"
              >
                Browse Properties
              </Link>
              <Link
                to="/listings?listing=short-term"
                className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-lg sm:rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:-translate-y-1 hover:shadow-xl btn-press flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <i className="ri-home-wifi-line"></i>
                Short-Term Stays
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 sm:py-16 px-4 sm:px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12">
            <div className="col-span-2 sm:col-span-2 lg:col-span-1">
              <img
                src="https://static.readdy.ai/image/8b99d679a4d9fbd968419a3fb7823edc/a028b9890daf6b5d8e9903ed555560ee.jpeg"
                alt="Juwa Real Estate"
                className="h-12 sm:h-16 w-auto mb-4 sm:mb-6 brightness-0 invert"
              />
              <p className="text-gray-400 leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base">{t('footer.aboutDesc')}</p>
              <div className="flex gap-2 sm:gap-3">
                <a href="#" className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-white/10 hover:bg-primary-600 rounded-lg transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-base sm:text-lg"></i>
                </a>
                <a href="#" className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-white/10 hover:bg-primary-600 rounded-lg transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-base sm:text-lg"></i>
                </a>
                <a href="#" className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-white/10 hover:bg-primary-600 rounded-lg transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-base sm:text-lg"></i>
                </a>
                <a href="#" className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-white/10 hover:bg-primary-600 rounded-lg transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-base sm:text-lg"></i>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-base sm:text-lg font-bold mb-4 sm:mb-6">Quick Links</h3>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <Link to="/" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm sm:text-base">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/listings" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm sm:text-base">
                    Listings
                  </Link>
                </li>
                <li>
                  <Link to="/agents" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm sm:text-base">
                    Agents
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm sm:text-base">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/testimonials" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm sm:text-base">
                    Testimonials
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-base sm:text-lg font-bold mb-4 sm:mb-6">Services</h3>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <Link to="/listings?listing=rent" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm sm:text-base">
                    For Rent
                  </Link>
                </li>
                <li>
                  <Link to="/listings?listing=sale" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm sm:text-base">
                    For Sale
                  </Link>
                </li>
                <li>
                  <Link to="/listings?listing=short-term" className="text-gray-400 hover:text-white transition-colors cursor-pointer flex items-center gap-1.5 text-sm sm:text-base">
                    <i className="ri-home-wifi-line text-amber-400"></i>
                    Short-Term
                  </Link>
                </li>
                <li>
                  <Link to="/upload-property" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm sm:text-base">
                    List Property
                  </Link>
                </li>
                <li>
                  <Link to="/agents" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm sm:text-base">
                    Find Agent
                  </Link>
                </li>
              </ul>
            </div>

            <div className="col-span-2 sm:col-span-1">
              <h3 className="text-base sm:text-lg font-bold mb-4 sm:mb-6">Contact Us</h3>
              <ul className="space-y-3 sm:space-y-4">
                <li className="flex items-start gap-2 sm:gap-3">
                  <i className="ri-map-pin-line text-primary-500 mt-0.5"></i>
                  <span className="text-gray-400 text-sm sm:text-base">Juba, South Sudan</span>
                </li>
                <li className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-phone-line text-primary-500"></i>
                  <span className="text-gray-400 text-sm sm:text-base">+211 912 000 000</span>
                </li>
                <li className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-mail-line text-primary-500"></i>
                  <span className="text-gray-400 text-sm sm:text-base">info@juwa.ss</span>
                </li>
                <li className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-time-line text-primary-500"></i>
                  <span className="text-gray-400 text-sm sm:text-base">Mon - Sat: 8AM - 6PM</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-6 sm:pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <img
                src="https://static.readdy.ai/image/8b99d679a4d9fbd968419a3fb7823edc/a028b9890daf6b5d8e9903ed555560ee.jpeg"
                alt="Juwa"
                className="h-6 sm:h-8 w-auto brightness-0 invert"
              />
              <p className="text-gray-500 text-xs sm:text-sm">
                &copy; {new Date().getFullYear()} Juwa Real Estate
              </p>
            </div>
            <div className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm">
              <Link to="/cookie-policy" className="text-gray-500 hover:text-white transition-colors cursor-pointer">
                Privacy & Cookies
              </Link>
              <Link to="/policy" className="text-gray-500 hover:text-white transition-colors cursor-pointer">
                Terms & Policy
              </Link>
              <a href="https://readdy.ai/?ref=logo" className="text-gray-500 hover:text-white transition-colors cursor-pointer">
                Powered By Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
